import { useState } from 'react';

import { ButtonMantine, Center, Group, Image, Modal, Stack, Text } from 'ui';
import { Shield } from 'ui/Icons';

import type { ICredential } from '../../api';
import { getCardDate } from '../../hooks';
import { LearnMoreCardAccordion } from '../Accordions';

type Props = {
  credential?: ICredential;
  showFooter?: boolean;
  active?: boolean;
  onClick?: () => void;
};

type LearnMoreProps = {
  isLearnMoreOpen: boolean;
  setIsLearnMoreOpen: (boolean: boolean) => void;
};

type CardProps = {
  credential: ICredential;
  active?: boolean;
  onClick?: () => void;
};

const Card = ({ credential, active = false, onClick }: CardProps) => {
  return (
    <div className={['CreditCard w-full', active ? 'active' : ''].join(' ')} onClick={onClick}>
      <div className="mr-4 w-9">
        <Image src={credential?.image} style={{ width: 36 }} />
      </div>
      <div className="flex flex-col sm:mr-4 text-gray-500">
        <span className="text-xs f-ic font-medium capitalize">
          {`${credential.card_type || ''} `}
          {credential.display}
        </span>
        {credential.month && credential.year && (
          <span className="font-normal text-10">{`Expires on ${getCardDate(
            credential.month,
            credential.year
          )}`}</span>
        )}
      </div>
    </div>
  );
};

const LearnMore = ({ isLearnMoreOpen, setIsLearnMoreOpen }: LearnMoreProps) => {
  return (
    <Modal
      opened={isLearnMoreOpen}
      onClose={() => setIsLearnMoreOpen(false)}
      title="Learn More"
      variant="full-screen-mobile"
    >
      <LearnMoreCardAccordion variant="separated" />
    </Modal>
  );
};

export const TokenizedCredentialCard = ({
  credential,
  showFooter = false,
  active = false,
  onClick,
}: Props) => {
  return (
    <Stack
      bg={'#ECEFF3'}
      style={(theme) => ({ borderRadius: theme.radius.md, gap: 0, flexGrow: 1 })}
      align="stretch"
    >
      {credential && <Card credential={credential} active={active} onClick={onClick} />}
      {showFooter && <CardTokenizationFaqBadge />}
    </Stack>
  );
};

export const CardTokenizationFaqBadge = () => {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  return (
    <Group gap="xs" p="xs" bg="gray.0" pr={0} style={{ borderRadius: '4px' }} wrap="nowrap">
      <Center style={{ flexShrink: 0 }}>
        <Shield />
      </Center>
      <Text fz={10} style={{ flexGrow: 1 }}>
        We’re serious about security and our commitment to keep your data safe.
      </Text>
      <ButtonMantine size="xs" variant="subtle" onClick={() => setIsLearnMoreOpen(true)}>
        Learn More
      </ButtonMantine>
      <LearnMore isLearnMoreOpen={isLearnMoreOpen} setIsLearnMoreOpen={setIsLearnMoreOpen} />
    </Group>
  );
};
